import { useEffect, useState } from "react";
import { stopImpersonation, currentImpersonation } from "../lib/api";
import { UserCog, XCircle } from "lucide-react";

/**
 * Sticky banner rendered on every route when the current session is an
 * admin-impersonation shadow (Bearer token stored in sessionStorage). Clicking
 * "Exit impersonation" wipes the token and reloads — the admin's cookie
 * session is untouched, so they're immediately back on their own account.
 *
 * Auto-hides when no impersonation is active. Auto-expires visually after the
 * 30-min server-side TTL — the next API call will 401 and boot the shadow.
 */
export default function ImpersonationBanner() {
  const [imp, setImp] = useState(() => currentImpersonation());

  useEffect(() => {
    // Re-check on tab focus (someone may have stopped impersonation from another window).
    const onFocus = () => setImp(currentImpersonation());
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (!imp) return null;
  const t = imp.meta || {};

  const exit = () => {
    stopImpersonation();
    // Hard reload so the AuthContext re-hydrates from the admin's cookie session.
    window.location.href = "/";
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[60] w-full bg-[#FF9F0A] text-black px-4 py-2 flex items-center justify-center gap-4 text-xs uppercase tracking-widest font-bold shadow-lg"
    >
      <UserCog className="size-4" strokeWidth={2.5} />
      <span data-testid="impersonation-banner-text">
        Viewing as <span className="font-mono">{t.name || t.email}</span>
        {t.role && <> · <span className="font-mono">{t.role}</span></>}
        {t.client_name && <> · {t.client_name}</>}
      </span>
      <button
        type="button"
        onClick={exit}
        data-testid="impersonation-exit"
        className="inline-flex items-center gap-1.5 px-3 py-1 bg-black/80 text-[#FF9F0A] hover:bg-black transition-colors"
      >
        <XCircle className="size-3.5" strokeWidth={2.5} /> Exit impersonation
      </button>
    </div>
  );
}
